// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { I18n } from '@calendly/i18n'
import { useLayoutEffect } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'

import { useGetUserQuery } from 'common'
import { LoadingWrapper } from 'components/core/loading_state'
import { PageContainer, PageHeaderWithTabs } from 'components/page_elements'
import { isDataLoadingSelector, Login, Profile } from 'features/account_settings'
import { generateTabs } from 'features/admin'
import { parseSettingsTabFromSearch } from 'features/settings_modal/constants'
import { useIsSettingsModalLayout, useSettingsModalLayoutOptional } from 'features/settings_modal/layout_context'

import {
  generateBrowserTabs,
  legacyProfileNestedBrowserRedirectTarget,
  PROFILE_SETTINGS_BASE,
} from '../personal_browser_urls'
import { getProfileSettingsBrowserTabs, getProfileSettingsTabs } from '../settings_section_tabs'

const GeneralTab = () => {
  const accountDataLoading = useSelector(isDataLoadingSelector)
  const accountHydrated = useSelector((state) => {
    const p = state.account_settings?.profile
    return Boolean(p?.slug || p?.email)
  })
  const { data: user, isLoading: isUserQueryLoading } = useGetUserQuery()
  const accountBlocking = !accountHydrated && accountDataLoading
  const dataPending = accountBlocking || isUserQueryLoading || !user

  return (
    <LoadingWrapper isLoading={dataPending}>
      <Profile />
    </LoadingWrapper>
  )
}

const LoginTab = () => (
  <Login />
)

const resolveProfileBrowserTab = (search) => {
  const tabSlug = parseSettingsTabFromSearch(search)
  return tabSlug === 'login' ? 'login' : 'general'
}

export const ProfileSettingsPage = () => {
  const { pathname, search, hash } = useLocation()
  const inSettingsModal = useIsSettingsModalLayout()
  const layout = useSettingsModalLayoutOptional()
  const settingsModalShellOwnsTabs = inSettingsModal

  const legacyBrowserRedirect = legacyProfileNestedBrowserRedirectTarget(pathname, search, hash)
  const activeBrowserTab = resolveProfileBrowserTab(search)

  useLayoutEffect(() => {
    if (layout?.setFooterSuppressed == null) {
      return undefined
    }
    const suppress = inSettingsModal
      ? pathname === `${PROFILE_SETTINGS_BASE}/login`
      : activeBrowserTab === 'login'
    layout.setFooterSuppressed(suppress)
    return () => layout.setFooterSuppressed(false)
  }, [activeBrowserTab, inSettingsModal, layout, pathname])

  if (!inSettingsModal && legacyBrowserRedirect != null) {
    return <Redirect to={legacyBrowserRedirect} />
  }

  const memoryTabs = getProfileSettingsTabs()
  const browserTabs = getProfileSettingsBrowserTabs()
  const tabSelection = inSettingsModal
    ? generateTabs({ pathname, tabs: memoryTabs })
    : generateBrowserTabs({ pathname, search, tabs: browserTabs })

  const memoryRoutes = (
    <PageContainer>
      <Switch>
        <Redirect exact from={PROFILE_SETTINGS_BASE} to={`${PROFILE_SETTINGS_BASE}/general`} />
        <Route path={`${PROFILE_SETTINGS_BASE}/general`} component={GeneralTab} />
        <Route path={`${PROFILE_SETTINGS_BASE}/login`} component={LoginTab} />
      </Switch>
    </PageContainer>
  )

  const browserRoutes = (
    <PageContainer>
      {activeBrowserTab === 'login' ? <LoginTab /> : <GeneralTab />}
    </PageContainer>
  )

  const routes = inSettingsModal ? memoryRoutes : browserRoutes

  if (settingsModalShellOwnsTabs) {
    return routes
  }

  return (
    <>
      <PageHeaderWithTabs
        hideTitleChrome={inSettingsModal}
        eyebrow={I18n.t('publisher.personal_settings.navigation.account_details')}
        title={I18n.t('publisher.account_settings.navigation.profile')}
        tabs={tabSelection}
      />
      {routes}
    </>
  )
}
